import { FC, Fragment, useState, useEffect, useRef, useMemo } from 'react';
import './SimulationBank.scss';
import '../ChatQuiz/ChatQuiz.scss';
import { BvIcon } from 'bevi-icon';
import { useSimulationBank } from '../../../providers/SimulationBank/SimulationBankProvider';
import { useAuth } from '../../../providers/AuthProvider';
import Step1Form from './steps/Step1Form/Step1Form';
import Step2Loading from './steps/Step2Loading/Step2Loading';
import Step3Cards from './steps/Step3Cards/Step3Cards';
import Step4Params from './steps/Step4Params/Step4Params';
import Step4Results, { SortIcon } from './steps/Step4Results/Step4Results';
import StepDigitacaoForm from './steps/StepDigitacao/StepDigitacaoForm';
import StepConfirmacao from './steps/StepConfirmacao/StepConfirmacao';
import StepResultado from './steps/StepResultado/StepResultado';
import Clt from '../../../assets/icons/clt.svg'
import Inss from '../../../assets/icons/inss.svg'
import { hasValidSimulationSession } from '../../../utils/SimulationSessionCache';
import { applyMaskCPF, applyMaskPhone } from './steps/utils/masks';
import { CityOption, FinanceiraOption, HistoricoSimulacaoItem, PropostaConsultaItem, StateOption } from '../../../models/SimulationBank/SimulationBankModels';
import { LocationsService } from '../../../services/SimulationBank/LocationsService';
import { matchStateOption } from './steps/Step1Form/clientLookup';
import ModalDefault from '../../ModalDefault/ModalDefault';
import { canAccessDataprevFeatures } from '../../../utils/dataprevAccess';

const DATAPREV_PRODUCT_OPTIONS: { value: 'clt' | 'inss' | 'ambos'; label: string }[] = [
    { value: 'clt', label: 'Consignado do Trabalhador (CLT)' },
    { value: 'inss', label: 'INSS' },
    { value: 'ambos', label: 'Ambos' },
];
interface SimulationBankProps {
    onClose: () => void;
    onStepLabelChange?: (label: string | null) => void;
    onTitleChange?: (title: string | null) => void;
    // Quando true, o componente já monta direto na fase 'query' (Consulta de Proposta),
    // pulando a seleção de produto/operação do fluxo de simulação CLT — usado pelo
    // assistente "Consulta de Propostas" da sidebar (ver ASSISTANT_BEHAVIOR em Chats.tsx).
    startInQuery?: boolean;
}

// Gambiarra: o header do chat (Chats.tsx) é um template compartilhado por todos os
// agentes e fica fora do SimulationBankProvider, então não tem acesso à `step`/`phase`
// via context. Reportamos o label calculado aqui para cima via callback (ver onStepLabelChange).
const ALL_STEPS = [1, 3, 4, 5, 6, 7];
const TOTAL_STEPS = ALL_STEPS.length;

const STEP_SUBTITLES: Record<number, string> = {
    1: 'Consulta de Margem',
    3: 'Seleção de Financeira',
    4: 'Parâmetros de Simulação',
    5: 'Resultados da Simulação',
    6: 'Digitação de Proposta',
    7: 'Efetivação da Proposta',
};

const isLoadingStep = (step: number): boolean => [2, 4.5].includes(step);

const getStepHeaderLabel = (step: number): string | null => {
    if (!ALL_STEPS.includes(step)) return null;
    const stepIndex = ALL_STEPS.indexOf(step);
    const etapaNumber = stepIndex + 1;
    const subtitle = STEP_SUBTITLES[step] || 'Proposta';
    return `Etapa ${etapaNumber}/${TOTAL_STEPS} - ${subtitle}`;
};

const getHeaderTitle = (step: number): string | null => {
    if (isLoadingStep(step)) return null;
    const title = STEP_HEADER_TITLE[String(step)] ?? DEFAULT_HEADER_TITLE;
    return title;
};

// Sobrescreve o título do header (que por padrão reflete o nome do agente na sidebar,
// ex.: "Simulação e Digitação de Proposta") enquanto a simulação estiver aberta — a sidebar
// em si não muda, só o título mostrado no topo do chat (ver onTitleChange).
const DEFAULT_HEADER_TITLE = 'Simulação e Digitação de Proposta';
const QUERY_HEADER_TITLE = 'Consulta de Proposta';
const STEP_HEADER_TITLE: Record<string, string> = {
    '1': 'Simulação de Proposta',
    '3': 'Simulação de Proposta',
    '4': 'Simulação de Proposta',
    '5': 'Simulação de Proposta',
    '6': 'Digitação da Proposta',
    '7': 'Digitação da Proposta',
};

type Phase = 'product' | 'operation' | 'loading' | 'done' | 'query';
type TextSeg = { text: string; style?: 'bold' | 'cyan' | 'note'; icon?: string };

interface ChatMsg {
    id: string;
    type: 'bot' | 'user';
    segments?: TextSeg[];
    content?: string;
    time: string;
    onStreamDone?: () => void;
    // Resultados da Consulta de Propostas (simulation/digitations/list), anexados à
    // mensagem do bot que os apresenta — ver handle*Submit em SimulationBank.
    results?: PropostaConsultaItem[];
    // Idem para o ramo "Histórico de Simulações" (simulation/screening/history).
    historico?: HistoricoSimulacaoItem[];
}

// Opção do select de financeira na Consulta de Propostas por número — as bancarizadoras
// da Soma (SOMA_UY3, SOMA_CELCOIN, ...) são exibidas como uma única financeira "Soma",
// já que o usuário não precisa saber qual bancarizadora processou a proposta. canonicalId
// é o valor enviado no filtro "financial" da consulta — para o grupo Soma é sempre 'SOMA'
// (fixo, combinado com o time), nunca o canonical_id de uma bancarizadora específica.
interface FinanceiraSelectOption {
    id: string;
    name: string;
    ids: number[];
    canonicalId: string;
}

const groupFinanceirasForSelect = (list: FinanceiraOption[]): FinanceiraSelectOption[] => {
    const soma = list.filter((f) => f.canonical_id?.toUpperCase().startsWith('SOMA'));
    const others = list.filter((f) => !f.canonical_id?.toUpperCase().startsWith('SOMA'));

    const options: FinanceiraSelectOption[] = others.map((f) => ({
        id: String(f.id),
        name: f.name,
        ids: [f.id],
        canonicalId: f.canonical_id,
    }));

    if (soma.length > 0) {
        options.push({ id: 'SOMA', name: 'Soma', ids: soma.map((f) => f.id), canonicalId: 'SOMA' });
    }

    return options;
};

// O filtro "financial" de simulation/digitations/list não reconhece o canonical_id "PRESENCA"
// (catálogo simulation/screening/financials, mesmo valor usado nos logos/cores) — o backend
// espera o texto exato salvo no campo "financeira" das propostas dessa financeira: "Presença
// Bank". Sem esse alias, a busca por número de proposta não encontra nada pra essa financeira
// mesmo quando existem propostas (a busca por CPF não é afetada, pois não envia "financial").
const DIGITATION_FINANCIAL_ALIAS: Record<string, string> = {
    PRESENCA: 'Presença Bank',
};

const toDigitationFinancial = (canonicalId?: string): string | undefined =>
    canonicalId ? (DIGITATION_FINANCIAL_ALIAS[canonicalId.toUpperCase()] ?? canonicalId) : undefined;

const formatDateTimeBR = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const formatCurrencyBRL = (value: number | null): string | null => {
    if (value == null) return null;
    return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const stripAccents = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

const propostaStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'neutral' => {
    const s = stripAccents(status).toUpperCase();
    if (s.includes('CANCELAD') || s.includes('REPROVAD')) return 'danger';
    if (s.includes('APROVAD') || s.includes('ASSINATURA') || s.includes('CONCLUID') || s.includes('EFETIVAD')) return 'success';
    if (s.includes('ANALISE') || s.includes('PENDENTE') || s.includes('AGUARD')) return 'warning';
    return 'neutral';
};

// Variante de cor pros badges da linha do tempo (histórico bruto de eventos da proposta) —
// paleta própria, mais alinhada ao mock (azul/lavanda pra "aguardando", vermelho/rosa pra
// cancelamento) do que as cores usadas no status "resumo" da tabela principal.
const timelineStatusVariant = (status: string): 'info' | 'danger' | 'neutral' => {
    const s = stripAccents(status).toUpperCase();
    if (s.includes('CANCELAD')) return 'danger';
    if (s.includes('AGUARD')) return 'info';
    return 'neutral';
};

const formatDateTimeSecondsBR = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const date = d.toLocaleDateString('pt-BR');
    const time = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
    return `${date} às ${time}`;
};

// "Digitada em"/"Atualizada em" no Espelho da Proposta — formato "DD/MM/AAAA às HHhMM",
// diferente do formatDateTimeBR usado na tabela principal de resultados.
const formatDateTimeHourBR = (iso: string): string => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const date = d.toLocaleDateString('pt-BR');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${date} às ${hours}h${minutes}`;
};

const FINANCEIRA_DOT_COLORS: Record<string, string> = {
    FACTA: '#7C6FF0',
    CREFAZ: '#17B6C4',
    SOMA: '#F5A623',
    C6: '#1F2A44',
    PRESENCA: '#E4578C',
};

const financeiraDotColor = (financeira: string): string =>
    FINANCEIRA_DOT_COLORS[financeira?.toUpperCase()] ?? '#9CA3AF';

const titleCase = (s: string): string =>
    s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s;

// Normaliza o status pra "Primeira Maiúscula" em cada palavra — algumas financeiras (ex.: C6)
// mandam tudo em caixa alta ("SEM COMISSAO"), o que fica estranho lado a lado com outras
// que já vêm bem formatadas (ex.: "Aguardando Assinatura").
const titleCaseWords = (s: string): string =>
    s ? s.toLowerCase().replace(/(^|\s)\S/g, (c) => c.toUpperCase()) : s;

// Código e nome de quem digitou a proposta, pra coluna "Digitador". A esteira só preenche
// "digitador" quando quem digitou foi um dependente; quando é o próprio master (ou um
// operador indicado na efetivação por um não comissionado) o código vem em
// agente.codigo_indicado. Só as propostas antigas o bastante pra não ter nenhum dos dois
// ficam sem código, e aí a célula mostra "—".
const propostaDigitador = (p: PropostaConsultaItem): { codigo: string; nome: string | null } | null => {
    if (p.digitador?.dependent_id != null) {
        return { codigo: String(p.digitador.dependent_id), nome: p.digitador.nome ?? null };
    }
    if (p.agente?.codigo_indicado) {
        return { codigo: p.agente.codigo_indicado, nome: p.agente.parceiro?.nome ?? null };
    }
    return null;
};

type PropostaColumnKey =
    | 'cpf' | 'client_name' | 'digitador' | 'financeira' | 'proposal_number' | 'status'
    | 'created_at' | 'status_updated_at' | 'valor_total' | 'prazo' | 'formalization_url';

const PROPOSTA_COLUMNS: { key: PropostaColumnKey; label: string }[] = [
    { key: 'formalization_url', label: 'Link de Formalização' },
    { key: 'cpf', label: 'CPF' },
    { key: 'client_name', label: 'Nome' },
    { key: 'digitador', label: 'Digitador' },
    { key: 'financeira', label: 'Financeira' },
    { key: 'proposal_number', label: 'Nº Proposta' },
    { key: 'status', label: 'Status' },
    { key: 'created_at', label: 'Data de Digitação' },
    { key: 'status_updated_at', label: 'Data de Alteração' },
    { key: 'valor_total', label: 'Valor' },
    { key: 'prazo', label: 'Prazo' },
];

const comparePropostas = (a: PropostaConsultaItem, b: PropostaConsultaItem, key: PropostaColumnKey): number => {
    // "digitador" não é um campo simples do item (ver propostaDigitador) — ordenamos pelo
    // código exibido, numericamente quando os dois lados são numéricos e alfabeticamente
    // caso contrário; propostas sem código ficam por último.
    if (key === 'digitador') {
        const ac = propostaDigitador(a)?.codigo;
        const bc = propostaDigitador(b)?.codigo;
        if (ac == null && bc == null) return 0;
        if (ac == null) return 1;
        if (bc == null) return -1;
        const an = Number(ac);
        const bn = Number(bc);
        if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
        return ac.localeCompare(bc, 'pt-BR');
    }

    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;

    if (key === 'created_at' || key === 'status_updated_at') {
        return new Date(av as string).getTime() - new Date(bv as string).getTime();
    }
    if (key === 'valor_total' || key === 'prazo') {
        return (av as number) - (bv as number);
    }
    return String(av).localeCompare(String(bv), 'pt-BR');
};

// Tabela de resultados da Consulta de Propostas — cada mensagem do bot tem sua própria
// instância/estado de ordenação, então isso fica de fora do SimulationBank (que só guarda
// os dados crus em ChatMsg.results).
const PropostaResultsTable: FC<{ results: PropostaConsultaItem[]; onSelect: (p: PropostaConsultaItem) => void }> = ({ results, onSelect }) => {
    const [sortKey, setSortKey] = useState<PropostaColumnKey | null>(null);
    const [sortAsc, setSortAsc] = useState(true);
    // Feedback do botão "Copiar" da coluna Formalização — mesmo padrão do StepResultado
    // (handleCopyLink/copiedLink), mas por instância de tabela.
    const [copiedLink, setCopiedLink] = useState<string | null>(null);

    const sorted = useMemo(() => {
        if (!sortKey) return results;
        const copy = [...results];
        copy.sort((a, b) => comparePropostas(a, b, sortKey) * (sortAsc ? 1 : -1));
        return copy;
    }, [results, sortKey, sortAsc]);

    // 3 estados por coluna (igual à tabela de simulação das financeiras, ver handleSort em
    // Step4Results.tsx): 1º clique ordena asc, 2º clique na mesma coluna vira desc, 3º clique
    // volta pra ordem padrão (sortKey null).
    const handleSort = (key: PropostaColumnKey) => {
        if (sortKey !== key) { setSortKey(key); setSortAsc(true); return; }
        if (sortAsc) { setSortAsc(false); return; }
        setSortKey(null);
        setSortAsc(true);
    };

    const handleCopyLink = (link: string) => {
        navigator.clipboard.writeText(link);
        setCopiedLink(link);
        setTimeout(() => setCopiedLink(null), 2000);
    };

    return (
        <div className="sim-proposta-table-wrap">
            <table className="sim-proposta-table">
                <thead>
                    <tr>
                        {/* Coluna de ação (abre o mesmo modal que o clique na linha) — sem dado
                            próprio pra ordenar, por isso fica fora de PROPOSTA_COLUMNS/handleSort. */}
                        <th className="sim-proposta-table__espelho-col" aria-label="Espelho">Espelho</th>
                        {PROPOSTA_COLUMNS.map((col) => (
                            <th key={col.key} onClick={() => handleSort(col.key)}>
                                {col.label} <SortIcon dir={sortKey === col.key ? (sortAsc ? 'asc' : 'desc') : null} active={sortKey === col.key} />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((p) => (
                        <tr key={p.id} onClick={() => onSelect(p)}>
                            <td className="sim-proposta-table__espelho-cell" onClick={(e) => e.stopPropagation()}>
                                <button
                                    type="button"
                                    className="sim-proposta-table__espelho-btn"
                                    onClick={() => onSelect(p)}
                                >
                                    Abrir Espelho
                                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </button>
                            </td>
                            <td className="sim-proposta-table__formalizacao-cell" onClick={(e) => e.stopPropagation()}>
                                {p.formalization_url ? (
                                    <button
                                        type="button"
                                        className="sim-proposta-table__link-copy"
                                        title={p.formalization_url}
                                        onClick={() => handleCopyLink(p.formalization_url as string)}
                                    >
                                        {copiedLink === p.formalization_url ? 'Copiado!' : 'Copiar'}
                                    </button>
                                ) : '-'}
                            </td>
                            <td>{applyMaskCPF(p.cpf)}</td>
                            <td>{titleCaseWords(p.client_name)}</td>
                            {(() => {
                                const digitador = propostaDigitador(p);
                                return (
                                    <td title={digitador?.nome ? titleCaseWords(digitador.nome) : undefined}>
                                        {digitador?.codigo ?? '—'}
                                    </td>
                                );
                            })()}
                            <td>
                                <span className="sim-proposta-table__financeira">
                                    <span className="sim-proposta-table__dot" style={{ background: financeiraDotColor(p.financeira) }} />
                                    {titleCase(p.financeira)}
                                </span>
                            </td>
                            <td>{p.proposal_number}</td>
                            <td>
                                <span className={`sim-proposta-table__badge sim-proposta-table__badge--${propostaStatusVariant(p.status)}`}>
                                    {titleCaseWords(p.status)}
                                </span>
                            </td>
                            <td>{formatDateTimeBR(p.created_at)}</td>
                            <td>{formatDateTimeBR(p.status_updated_at)}</td>
                            <td>{formatCurrencyBRL(p.valor_total) ?? '-'}</td>
                            <td>{p.prazo != null ? `${p.prazo}x` : '-'}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ─── Histórico de Simulações (SB-1953) ───────────────────────────────────────────────

// Cliente escolhido numa linha do histórico, exibido no modal antes de abrir o formulário.
// Vem inteiro de DADOS_CLIENTE na própria listagem — sem consulta extra. Campo que a
// listagem não trouxer aparece como "—" e fica pro operador preencher.
interface PrefillCliente {
    cpf: string;
    nome_completo?: string;
    data_nascimento?: string;
    telefone?: string;
    cidade?: string;
    estado?: string;
}

const mapStateOptions = (response: any): StateOption[] =>
    (response?.data ?? response ?? []).map((s: any) => ({
        value: s.uf ?? s.sigla ?? s.id,
        label: s.nome ?? s.name ?? s.uf,
        uf: s.uf ?? s.sigla,
    }));

const formatBirthDateBR = (iso?: string): string => {
    if (!iso) return '—';
    const [y, m, d] = iso.split('-');
    return y && m && d ? `${d}/${m}/${y}` : iso;
};

type HistoricoColumnKey = 'data' | 'cpf' | 'nome' | 'modo' | 'parcelas' | 'valor_solicitado' | 'valor_parcela';

const HISTORICO_COLUMNS: { key: HistoricoColumnKey; label: string }[] = [
    { key: 'data', label: 'Data / Hora' },
    { key: 'cpf', label: 'CPF' },
    { key: 'nome', label: 'Nome' },
    { key: 'modo', label: 'Modo' },
    { key: 'parcelas', label: 'Qtde Parcelas' },
    { key: 'valor_solicitado', label: 'Valor Solicitado' },
    { key: 'valor_parcela', label: 'Valor Parcela' },
];

const HISTORICO_MODO_LABELS: Record<string, string> = {
    VALOR_PARCELA: 'Valor Parcela',
    VALOR_DESEJADO: 'Valor Desejado',
    VALOR_TOTAL: 'Valor Total',
};

const historicoModoLabel = (modo?: string): string =>
    !modo ? '-' : (HISTORICO_MODO_LABELS[modo] ?? titleCaseWords(modo.replace(/_/g, ' ')));

// Ordena pelo dado cru, não pelo texto formatado: a data ordenada como string sairia por
// "01/12" antes de "02/01", e o valor por "R$ 1.000" antes de "R$ 90".
const compareHistorico = (a: HistoricoSimulacaoItem, b: HistoricoSimulacaoItem, key: HistoricoColumnKey): number => {
    if (key === 'cpf') return String(a.CPF_CLIENTE ?? '').localeCompare(String(b.CPF_CLIENTE ?? ''), 'pt-BR');
    if (key === 'nome') return String(a.DADOS_CLIENTE?.NOME ?? '').localeCompare(String(b.DADOS_CLIENTE?.NOME ?? ''), 'pt-BR');

    const as = a.SIMULACAO;
    const bs = b.SIMULACAO;
    const pick = (s?: typeof as): string | number | undefined => {
        if (!s) return undefined;
        if (key === 'data') return s.DATA_SIMULACAO ? new Date(s.DATA_SIMULACAO).getTime() : undefined;
        if (key === 'modo') return s.MODO;
        if (key === 'parcelas') return s.QTDE_PARCELAS;
        if (key === 'valor_solicitado') return s.VALOR_SOLICITADO;
        return s.VALOR_PARCELA;
    };

    const av = pick(as);
    const bv = pick(bs);
    // Linha sem o dado vai sempre para o fim, nos dois sentidos da ordenação.
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return av - bv;
    return String(av).localeCompare(String(bv), 'pt-BR');
};

// Tabela do histórico de simulações — uma linha por cliente. Espelha PropostaResultsTable
// (estado de ordenação por instância, ver comentário lá).
const HistoricoResultsTable: FC<{ results: HistoricoSimulacaoItem[]; onSimular: (item: HistoricoSimulacaoItem) => void }> = ({ results, onSimular }) => {
    const [sortKey, setSortKey] = useState<HistoricoColumnKey | null>(null);
    const [sortAsc, setSortAsc] = useState(true);

    const sorted = useMemo(() => {
        if (!sortKey) return results;
        const copy = [...results];
        copy.sort((a, b) => compareHistorico(a, b, sortKey) * (sortAsc ? 1 : -1));
        return copy;
    }, [results, sortKey, sortAsc]);

    // Mesmo ciclo de 3 estados do handleSort de PropostaResultsTable (ver comentário lá).
    const handleSort = (key: HistoricoColumnKey) => {
        if (sortKey !== key) { setSortKey(key); setSortAsc(true); return; }
        if (sortAsc) { setSortAsc(false); return; }
        setSortKey(null);
        setSortAsc(true);
    };

    return (
        <div className="sim-proposta-table-wrap">
            <table className="sim-proposta-table sim-proposta-table--historico">
                <thead>
                    <tr>
                        {HISTORICO_COLUMNS.map((col) => (
                            <th key={col.key} onClick={() => handleSort(col.key)}>
                                {col.label} <SortIcon dir={sortKey === col.key ? (sortAsc ? 'asc' : 'desc') : null} active={sortKey === col.key} />
                            </th>
                        ))}
                        <th className="sim-proposta-table__action-col" aria-label="Ações" />
                    </tr>
                </thead>
                <tbody>
                    {sorted.map((item, i) => {
                        const s = item.SIMULACAO;
                        return (
                            <tr key={`${item.CPF_CLIENTE}-${s?.JOURNEY_ID ?? i}`}>
                                <td>{s?.DATA_SIMULACAO ? formatDateTimeBR(s.DATA_SIMULACAO) : '-'}</td>
                                <td>{applyMaskCPF(item.CPF_CLIENTE ?? '')}</td>
                                <td>{item.DADOS_CLIENTE?.NOME ? titleCaseWords(item.DADOS_CLIENTE.NOME) : '-'}</td>
                                <td>{historicoModoLabel(s?.MODO)}</td>
                                <td>{s?.QTDE_PARCELAS != null ? `${s.QTDE_PARCELAS}x` : '-'}</td>
                                {/* Só o eixo simulado vem preenchido — ausência é "não se aplica", nunca zero. */}
                                <td>{s?.VALOR_SOLICITADO != null ? formatCurrencyBRL(s.VALOR_SOLICITADO) : '-'}</td>
                                <td>{s?.VALOR_PARCELA != null ? formatCurrencyBRL(s.VALOR_PARCELA) : '-'}</td>
                                <td className="sim-proposta-table__action-cell">
                                    <button
                                        type="button"
                                        className="sim-proposta-table__action"
                                        title="Iniciar nova simulação para este cliente"
                                        onClick={() => onSimular(item)}
                                    >
                                        {/* Ícone de teclado do protótipo — o bevi-icon não tem um, então
                                            vai inline, como o SVG do chip "Continuar". */}
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                            <path d="M20 5H4c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 12H4V7h16v10zM11 8h2v2h-2V8zm0 3h2v2h-2v-2zM8 8h2v2H8V8zm0 3h2v2H8v-2zm-3 0h2v2H5v-2zm0-3h2v2H5V8zm3 6h8v2H8v-2zm6-3h2v2h-2v-2zm0-3h2v2h-2V8zm3 3h2v2h-2v-2zm0-3h2v2h-2V8z" />
                                        </svg>
                                    </button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
};

const StreamingText: FC<{ segments: TextSeg[]; speed?: number; onComplete?: () => void; instant?: boolean }> = ({
    segments, speed = 22, onComplete, instant = false,
}) => {
    const totalChars = segments.reduce((s, seg) => s + seg.text.length, 0);
    const [count, setCount] = useState(() => instant ? totalChars : 0);

    useEffect(() => {
        if (instant) { onComplete?.(); return; }
        setCount(0);
        let called = false;
        const iv = setInterval(() => {
            setCount(prev => {
                const next = Math.min(prev + 3, totalChars);
                if (next >= totalChars && !called) {
                    called = true;
                    clearInterval(iv);
                    setTimeout(() => onComplete?.(), 0);
                }
                return next;
            });
        }, speed);
        return () => clearInterval(iv);
    }, [segments]);

    let rem = count;
    const nodes = segments.map((seg, i) => {
        if (rem <= 0) return null;
        const visible = seg.text.slice(0, rem);
        rem -= seg.text.length;
        if (!visible) return null;
        const renderWithBreaks = (t: string, wrapper?: (c: React.ReactNode) => React.ReactNode) => {
            const parts = t.split('\n');
            const content = parts.map((p, j) => (
                <span key={j}>{p}{j < parts.length - 1 && <br />}</span>
            ));
            return wrapper ? wrapper(content) : content;
        };
        const content = renderWithBreaks(visible);
        let rendered = content;
        if (seg.style === 'bold') rendered = <strong>{content}</strong>;
        else if (seg.style === 'cyan') rendered = <strong style={{ color: '#19c9dd' }}>{content}</strong>;
        else if (seg.style === 'note') rendered = <span className="sim-intro-message__note">{content}</span>;

        if (seg.icon) {
            return (
                <div key={i} style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                    <BvIcon name={seg.icon} style={{ minWidth: 16, flexShrink: 0, color: '#25CBDB', marginTop: '2px' }} width={16} height={16} />
                    <span>{rendered}</span>
                </div>
            );
        }
        return <span key={i}>{rendered}</span>;
    });

    return <>{nodes}</>;
};

const INTRO_FINANCEIRAS: TextSeg[] = [
    { text: 'Após a autorização do cliente, ' },
    { text: 'aguarde nesta tela', style: 'bold' },
    { text: ' enquanto processamos a consulta. Em até ' },
    { text: '2 minutos', style: 'bold' },
    { text: ', as financeiras serão movidas automaticamente para as ' },
    { text: 'opções de simulação', style: 'bold' },
    { text: '.' },
];

const INTRO_DIGITACAO: TextSeg[] = [
    { text: 'Preencha os dados abaixo e clique em próximo para digitar sua proposta no ' },
    { text: 'banco/financeira', style: 'bold' },
    { text: '.' },
];

const fmt = () =>
    new Date().toLocaleString('pt-BR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });

const FORM_CACHE_KEY = '@beviajuda:sim-step1-clt';
const FORM_CACHE_TTL = 60 * 60 * 1000;

(() => {
    try {
        const nav = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
        if (nav?.type === 'reload') localStorage.removeItem(FORM_CACHE_KEY);
    } catch {}
})();

const hasValidFormCache = (): boolean => {
    try {
        const raw = localStorage.getItem(FORM_CACHE_KEY);
        if (!raw) return false;
        const { savedAt } = JSON.parse(raw);
        return Date.now() - savedAt <= FORM_CACHE_TTL;
    } catch { return false; }
};

const SimulationBank: FC<SimulationBankProps> = ({ onClose, onStepLabelChange, onTitleChange, startInQuery }) => {
    const { step, setStep, reset, competenceWindowMessage, checkScreeningAvailability, setOfertaSelecionada, getDataprevBlockStatus, setDataprevBlockStatus, listFinanceirasParaConsulta, queryPropostas, queryHistoricoSimulacoes, isLoadingDigitacaoForm, isValidatingDigitacao, isSubmittingProposta } = useSimulationBank();
    const { userLogged } = useAuth();
    const firstName = userLogged?.name?.split(' ')[0] || 'usuário';
    const canManageDataprev = canAccessDataprevFeatures(userLogged?.partner_code);

    const prevStepRef = useRef<number | null>(null);

    const [fromCache] = useState(() => hasValidFormCache() || hasValidSimulationSession());
    const [phase, setPhase] = useState<Phase>(() => startInQuery ? 'query' : ((hasValidFormCache() || hasValidSimulationSession()) ? 'done' : 'product'));
    const [chipsLocked, setChipsLocked] = useState(false);
    const [currentChips, setCurrentChips] = useState<'product' | 'operation' | 'query' | 'finish' | null>(null);
    const [loadingText, setLoadingText] = useState('Carregando Módulo de Simulações CLT...');
    const [formVisible, setFormVisible] = useState(() => hasValidFormCache() || hasValidSimulationSession());
    const [dataprevBlocked, setDataprevBlockedState] = useState<boolean | null>(null);
    const [dataprevOptionsOpen, setDataprevOptionsOpen] = useState(false);
    const [dataprevActionLoading, setDataprevActionLoading] = useState(false);
    // Loading interno do cálculo de comissão (Step4Results) — não muda `step`, então
    // precisa de um sinal próprio pra esconder título/subtítulo (ver isLoadingStep).
    const [isCommissionLoading, setIsCommissionLoading] = useState(false);
    // Filtros da Consulta de Propostas — queryMethod controla qual filtro está ativo.
    const [queryMethod, setQueryMethod] = useState<'digitacao' | 'alteracao' | 'cpf' | 'proposta' | null>(null);
    const [queryDateStart, setQueryDateStart] = useState('');
    const [queryDateEnd, setQueryDateEnd] = useState('');
    const [queryCpf, setQueryCpf] = useState('');
    // Filtro por "Número de Proposta": primeiro escolhe a financeira (select vindo de
    // simulation/screening/financials), depois informa o número.
    const [queryFinanceiras, setQueryFinanceiras] = useState<FinanceiraSelectOption[]>([]);
    const [isLoadingQueryFinanceiras, setIsLoadingQueryFinanceiras] = useState(false);
    const [queryFinanceiraId, setQueryFinanceiraId] = useState('');
    // submitted vira true assim que o usuário clica em "Continuar" (esconde o select na hora);
    // confirmed só vira true quando a próxima pergunta do bot termina de "digitar" (mostra o
    // campo de número). Sem o "submitted", o select ficava visível durante o streaming da
    // próxima mensagem, já que só "confirmed" desligava ele.
    const [queryFinanceiraSubmitted, setQueryFinanceiraSubmitted] = useState(false);
    const [queryFinanceiraConfirmed, setQueryFinanceiraConfirmed] = useState(false);
    const [queryPropostaNumero, setQueryPropostaNumero] = useState('');
    // Loading da chamada real a simulation/digitations/list (qualquer um dos 4 filtros).
    const [isSearchingPropostas, setIsSearchingPropostas] = useState(false);
    // Loading do "Histórico" do fluxo CLT (simulation/screening/history).
    const [isSearchingHistorico, setIsSearchingHistorico] = useState(false);
    // Cliente escolhido no histórico, exibido no modal de confirmação antes de abrir o
    // formulário pré-preenchido (ver handleSimularDoHistorico).
    const [prefillCliente, setPrefillCliente] = useState<PrefillCliente | null>(null);
    // Resolução do Estado contra a lista real, feita ao confirmar o modal.
    const [isConfirmingPrefill, setIsConfirmingPrefill] = useState(false);
    // Loading do botão "Atualizar" — reexecuta a última busca sem novas mensagens no chat.
    const [isRefreshingPropostas, setIsRefreshingPropostas] = useState(false);
    // Payload da última busca de propostas executada, pra permitir "Atualizar" sem refazer os filtros.
    const lastQueryPayloadRef = useRef<Record<string, any> | null>(null);
    // Id da mensagem do bot que carrega os resultados da última busca, pra "Atualizar"
    // substituir a tabela na mesma mensagem em vez de adicionar uma nova ao chat.
    const lastResultsMsgIdRef = useRef<string | null>(null);
    // Proposta clicada na tabela de resultados — abre o modal de detalhes (ver PropostaResultsTable).
    const [selectedProposta, setSelectedProposta] = useState<PropostaConsultaItem | null>(null);

    useEffect(() => {
        if (!canManageDataprev) return;
        getDataprevBlockStatus().then((blocked) => setDataprevBlockedState(blocked));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canManageDataprev]);

    useEffect(() => {
        // phase !== 'done' é a tela de seleção de produto/operação (fora do wizard de steps):
        // mantém o label padrão do agente ali, só troca quando entra de fato no wizard.
        if (phase === 'done') {
            if (isLoadingStep(step) || isSubmittingProposta || isCommissionLoading || isLoadingDigitacaoForm || isValidatingDigitacao) {
                onStepLabelChange?.(null);
                onTitleChange?.(null);
            } else {
                onStepLabelChange?.(getStepHeaderLabel(step));
                onTitleChange?.(getHeaderTitle(step));
            }
        } else if (phase === 'query') {
            onStepLabelChange?.(null);
            onTitleChange?.(QUERY_HEADER_TITLE);
        } else {
            onStepLabelChange?.(null);
            onTitleChange?.(DEFAULT_HEADER_TITLE);
        }
    }, [step, phase, isSubmittingProposta, isCommissionLoading, isLoadingDigitacaoForm, isValidatingDigitacao, onStepLabelChange, onTitleChange]);

    // Cleanup separado (deps vazias) para só limpar o label ao desmontar, não a cada troca de step
    useEffect(() => () => onStepLabelChange?.(null), [onStepLabelChange]);
    useEffect(() => () => onTitleChange?.(null), [onTitleChange]);

    useEffect(() => {
        const prevStep = prevStepRef.current;
        prevStepRef.current = step;

        // Só reage a uma transição real para o step 1 (ex.: reset/voltar), nunca ao mount
        // inicial — comparar contra o valor anterior (em vez de um flag "já rodou uma vez")
        // evita que o double-invoke de effects do StrictMode dispare isso na primeira renderização.
        if (prevStep === null || prevStep === step) return;

        if (step === 1) {
            // Não esconde o formulário aqui: ele depende do StreamingText do step 1
            // chamar onComplete para reaparecer, e isso não acontece ao voltar de outro
            // step (só no fluxo inicial do wizard), o que travava a tela em branco.
            try {
                localStorage.removeItem('@beviajuda:sim-step4-params');
            } catch {}
        }
    }, [step]);

    const introStep1 = useMemo<TextSeg[]>(() => [
        { text: 'Para simular um crédito CLT, preencha o formulário abaixo com os dados do cliente.' },
    ], [firstName]);

    const [messages, setMessages] = useState<ChatMsg[]>(() => startInQuery
        ? [{
            id: 'msg-0',
            type: 'bot',
            segments: [
                { text: 'Bem-vindo ao ' },
                { text: 'Agente de Consulta de Propostas do BevIAjuda', style: 'bold' },
                { text: '. Como você deseja fazer a consulta de hoje?' },
            ],
            time: fmt(),
            onStreamDone: () => setCurrentChips('query'),
        }]
        : [{
            id: 'msg-0',
            type: 'bot',
            segments: [
                { text: 'Bem-vindo ao agente de' },
                { text: ' Simulação e Digitação', style: 'bold' },
                { text: ' do BevIAjuda. Qual o produto que você quer simular hoje?' },
            ],
            time: fmt(),
            onStreamDone: () => setCurrentChips('product'),
        }]
    );

    const addMsg = (msg: Omit<ChatMsg, 'id'>) =>
        setMessages(prev => [...prev, { ...msg, id: String(Date.now()) }]);

    const restartToProductSelection = () => {
        localStorage.removeItem(FORM_CACHE_KEY);
        localStorage.removeItem('@beviajuda:sim-step4-params');
        reset();
        setMessages([{
            id: 'msg-0',
            type: 'bot',
            segments: [
                { text: 'Bem-vindo ao ' },
                { text: 'Agente de Simulações do BevIAjuda', style: 'bold' },
                { text: '. Qual o produto que você quer simular hoje?' },
            ],
            time: fmt(),
            onStreamDone: () => setCurrentChips('product'),
        }]);
        setPhase('product');
        setCurrentChips('product');
        setChipsLocked(false);
        setDataprevOptionsOpen(false);
    };

    const handleProduct = (product: 'clt' | 'inss') => {
        if (chipsLocked) return;
        setChipsLocked(true);
        setCurrentChips(null);

        if (product === 'inss') {
            addMsg({ type: 'user', content: 'INSS', time: fmt() });
            setTimeout(() => {
                addMsg({
                    type: 'bot',
                    segments: [{ text: 'O módulo de simulações de INSS estará disponível em breve. Por enquanto, selecione outro produto para continuar.' }],
                    time: fmt(),
                    onStreamDone: () => { setChipsLocked(false); setCurrentChips('product'); },
                });
            }, 600);
            return;
        }

        addMsg({ type: 'user', content: 'Consignado do Trabalhador (CLT)', time: fmt() });
        setTimeout(() => {
            addMsg({
                type: 'bot',
                segments: [
                    { text: 'Ok. Vamos seguir com o fluxo de simulações de ' },
                    { text: 'Consignado do Trabalhador (CLT)', style: 'bold' },
                    { text: ', mas antes me diga: você vai iniciar uma ' },
                    { text: 'simulação nova', style: 'bold' },
                    { text: '; continuar uma ' },
                    { text: 'simulação em andamento', style: 'bold' },
                    { text: ' ou consultar o ' },
                    { text: 'histórico de simulações', style: 'bold' },
                    { text: '?' },
                ],
                time: fmt(),
                onStreamDone: () => { setPhase('operation'); setChipsLocked(false); setCurrentChips('operation'); },
            });
        }, 600);
    };

    // Limpa a conversa atual e abre uma nova, no "agente" de Consulta de Propostas — disparado
    // pelo chip "Histórico" (ver handleOperation). Troca também o título do header (via
    // onTitleChange, ver efeito acima) para "Consulta de Proposta" enquanto a fase 'query'
    // estiver ativa.
    const startPropostaQuery = () => {
        setMessages([{
            id: 'msg-0',
            type: 'bot',
            segments: [
                { text: 'Bem-vindo ao ' },
                { text: 'Agente de Consulta de Propostas do BevIAjuda', style: 'bold' },
                { text: '. Como você deseja fazer a consulta de hoje?' },
            ],
            time: fmt(),
            onStreamDone: () => setCurrentChips('query'),
        }]);
        setPhase('query');
        setCurrentChips(null);
        setChipsLocked(false);
        setQueryMethod(null);
        setQueryDateStart('');
        setQueryDateEnd('');
        setQueryCpf('');
        setQueryFinanceiras([]);
        setQueryFinanceiraId('');
        setQueryFinanceiraSubmitted(false);
        setQueryFinanceiraConfirmed(false);
        setQueryPropostaNumero('');
        setDataprevOptionsOpen(false);
    };

    const handleQueryMethod = (method: 'digitacao' | 'alteracao' | 'cpf' | 'proposta') => {
        if (chipsLocked) return;
        setChipsLocked(true);
        setCurrentChips(null);

        const labels = {
            digitacao: 'Data de Digitação',
            alteracao: 'Data de Alteração',
            cpf: 'CPF do Cliente',
            proposta: 'Número de Proposta',
        };
        addMsg({ type: 'user', content: labels[method], time: fmt() });

        if (method === 'proposta') {
            setIsLoadingQueryFinanceiras(true);
            listFinanceirasParaConsulta().then((list) => {
                setQueryFinanceiras(groupFinanceirasForSelect(list));
                setIsLoadingQueryFinanceiras(false);
            });
            setTimeout(() => {
                addMsg({
                    type: 'bot',
                    segments: [{ text: 'Ótimo! Para fazer a busca de propostas por número, preciso que me informe primeiro a financeira que deseja consultar.' }],
                    time: fmt(),
                    onStreamDone: () => setQueryMethod('proposta'),
                });
            }, 600);
            return;
        }

        if (method === 'cpf') {
            setTimeout(() => {
                addMsg({
                    type: 'bot',
                    segments: [{ text: 'Ótimo! Para fazer a busca de propostas por CPF, preciso que me informe o CPF do cliente.' }],
                    time: fmt(),
                    onStreamDone: () => setQueryMethod('cpf'),
                });
            }, 600);
            return;
        }

        const introText = method === 'digitacao' ? 'data de digitação' : 'data de alteração';
        setTimeout(() => {
            addMsg({
                type: 'bot',
                segments: [{ text: `Ótimo! Para fazer a busca de propostas por ${introText}, preciso que me informe a data inicial e a data final da consulta.` }],
                time: fmt(),
                onStreamDone: () => setQueryMethod(method),
            });
        }, 600);
    };

    // Chamada real a simulation/digitations/list, compartilhada pelos 4 filtros — cada um
    // monta seu próprio payload e chama isto depois de já ter fechado seu formulário local.
    // isRefresh=true (botão "Atualizar") reexecuta o último payload e só substitui os dados
    // da tabela na mensagem já existente, sem adicionar novas mensagens ao chat.
    const runPropostaSearch = async (payload: Record<string, any>, isRefresh = false) => {
        lastQueryPayloadRef.current = payload;

        if (isRefresh) {
            setIsRefreshingPropostas(true);
            const result = await queryPropostas(payload);
            setIsRefreshingPropostas(false);

            if (result.success && lastResultsMsgIdRef.current) {
                const count = result.propostas.length;
                const text = count === 0
                    ? 'Não encontrei nenhuma proposta para esse filtro.'
                    : `Encontrei ${count} proposta${count > 1 ? 's' : ''} com os critérios informados. Você pode clicar no título de qualquer coluna para reordenar a tabela.`;
                const resultsMsgId = lastResultsMsgIdRef.current;

                setMessages((prev) => prev.map((m) => m.id === resultsMsgId
                    ? { ...m, segments: [{ text }], results: count > 0 ? result.propostas : undefined }
                    : m));
            }
            return;
        }

        addMsg({
            type: 'bot',
            segments: [{ text: 'Obrigado pelas informações! Aguarde enquanto consulto as propostas.' }],
            time: fmt(),
        });

        setIsSearchingPropostas(true);
        const result = await queryPropostas(payload);
        setIsSearchingPropostas(false);

        const count = result.propostas.length;
        const text = !result.success
            ? (result.message || 'Não foi possível buscar as propostas agora. Tente novamente em instantes.')
            : count === 0
                ? 'Não encontrei nenhuma proposta para esse filtro.'
                : `Encontrei ${count} proposta${count > 1 ? 's' : ''} com os critérios informados. Você pode clicar no título de qualquer coluna para reordenar a tabela.`;

        const resultsMsgId = String(Date.now());
        lastResultsMsgIdRef.current = result.success ? resultsMsgId : null;

        setMessages((prev) => [...prev, {
            id: resultsMsgId,
            type: 'bot',
            segments: [{ text }],
            time: fmt(),
            results: result.success && count > 0 ? result.propostas : undefined,
            onStreamDone: () => { setChipsLocked(false); setCurrentChips('finish'); },
        }]);
    };

    // Histórico de simulações (SB-1953) — vive no fluxo CLT (chip "Histórico" da fase
    // 'operation'), não na Consulta de Propostas: o histórico é só de CLT, enquanto a
    // Consulta passará a misturar INSS quando esse produto existir.
    // Não tem filtros: o endpoint do integrador não suporta busca por CPF nem por data.
    const runHistoricoSearch = async () => {
        addMsg({
            type: 'bot',
            segments: [{ text: 'Aguarde enquanto busco o histórico de simulações.' }],
            time: fmt(),
        });

        setIsSearchingHistorico(true);
        const result = await queryHistoricoSimulacoes();
        setIsSearchingHistorico(false);

        const text = !result.success
            ? (result.message || 'Não foi possível buscar o histórico de simulações agora. Tente novamente em instantes.')
            : result.simulacoes.length === 0
                ? 'Não encontrei nenhuma simulação no seu histórico.'
                // A busca traz uma página (50 por padrão); sem citar o total, a mensagem
                // afirmaria que o histórico inteiro cabe no que está na tela.
                : `Encontrei ${result.simulacoes.length} cliente${result.simulacoes.length > 1 ? 's' : ''} com simulação registrada${result.total > result.simulacoes.length ? `, dos ${result.total} mais recentes` : ''} — a última de cada um. Clique no ícone de teclado de uma linha para iniciar uma nova simulação com os dados do cliente, ou no título de qualquer coluna para reordenar a tabela.`;

        setMessages((prev) => [...prev, {
            id: String(Date.now()),
            type: 'bot',
            segments: [{ text }],
            time: fmt(),
            historico: result.success && result.simulacoes.length > 0 ? result.simulacoes : undefined,
            // Volta pros chips de operação: sem botão "Atualizar" próprio, reconsultar é
            // clicar em "Histórico" de novo.
            onStreamDone: () => { setChipsLocked(false); setCurrentChips('operation'); },
        }]);
    };

    // Ação "Simular" de uma linha do histórico: mostra no modal o cadastro que veio junto
    // da própria listagem (DADOS_CLIENTE), sem consulta extra.
    const handleSimularDoHistorico = (item: HistoricoSimulacaoItem) => {
        if (!item.CPF_CLIENTE) return;
        const dados = item.DADOS_CLIENTE;

        setPrefillCliente({
            cpf: applyMaskCPF(item.CPF_CLIENTE.replace(/\D/g, '')),
            nome_completo: dados?.NOME,
            // Já vem em yyyy-MM-dd, que é o formato do input[type="date"].
            data_nascimento: dados?.DATA_NASCIMENTO,
            // Vem só com os dígitos; o campo do formulário é mascarado.
            telefone: dados?.TELEFONE ? applyMaskPhone(dados.TELEFONE) : undefined,
            estado: dados?.ESTADO,
            cidade: dados?.CIDADE,
        });
    };

    // Confirmação do modal: grava o que foi mostrado no mesmo cache que o Step1Form lê no
    // seu mount. A marca `prefilledFromHistorico` diz de onde veio: com o cadastro
    // completo, o Step1Form pula a consulta por CPF, que não teria o que acrescentar.
    const handleConfirmarPrefill = async () => {
        if (!prefillCliente || isConfirmingPrefill) return;
        const { cpf, nome_completo, data_nascimento, telefone, estado, cidade } = prefillCliente;

        // Estado e Cidade são ReactSelect, não texto livre: gravar só o valor em
        // `formValues` faz os dois abrirem em branco. O Estado vem como sigla e precisa
        // virar a opção da lista real (é dela que sai o `uf` que o Step1Form usa pra
        // carregar as cidades); a Cidade segue o formato que o mapCities de lá produz,
        // com o nome servindo de value e label.
        setIsConfirmingPrefill(true);
        let stateOption: StateOption | null = null;
        if (estado) {
            const [statesResponse, statesError] = await new LocationsService().getStates();
            if (!statesError) stateOption = matchStateOption(mapStateOptions(statesResponse), estado);
        }
        const cityOption: CityOption | null = stateOption && cidade ? { value: cidade, label: cidade } : null;
        setIsConfirmingPrefill(false);

        try {
            localStorage.setItem(FORM_CACHE_KEY, JSON.stringify({
                formValues: {
                    cpf,
                    ...(nome_completo ? { nome_completo } : {}),
                    ...(data_nascimento ? { data_nascimento } : {}),
                    ...(telefone ? { telefone } : {}),
                    ...(stateOption ? { estado: stateOption.value } : {}),
                    ...(cityOption ? { cidade: cityOption.value } : {}),
                },
                selects: { state: stateOption, city: cityOption, sexo: null, alfa: null, mob: null, legado: null, tipoProduto: null },
                savedAt: Date.now(),
                prefilledFromHistorico: true,
            }));
        } catch {}

        setPrefillCliente(null);

        // Mesma checagem que o "Novo" faz: sem ela o parceiro preencheria um formulário
        // que a virada de competência da Dataprev não deixaria enviar.
        const isAvailable = await checkScreeningAvailability();
        if (!isAvailable) return;

        setStep(1);
        setFormVisible(true);
        setPhase('done');
    };

    // Encerra a Consulta de Propostas e volta pro início (boas-vindas + chips de filtro),
    // disparado pelo botão "Finalizar" que aparece depois de qualquer busca.
    const handleFinalizarConsulta = () => startPropostaQuery();

    // Reexecuta a última busca de propostas com os mesmos critérios, disparado pelo botão
    // "Atualizar" ao lado de "Finalizar" — não repete os filtros nem gera novas mensagens.
    const handleAtualizarConsulta = () => {
        if (!lastQueryPayloadRef.current || isRefreshingPropostas) return;
        runPropostaSearch(lastQueryPayloadRef.current, true);
    };

    const handleQuerySubmit = () => {
        if (!queryDateStart || !queryDateEnd) return;

        const fmtDate = (iso: string) => {
            const [y, m, d] = iso.split('-');
            return `${d}/${m}/${y}`;
        };
        const typeDate = queryMethod === 'alteracao' ? 'ALTERACAO' : 'DIGITACAO';

        addMsg({ type: 'user', content: `${fmtDate(queryDateStart)} até ${fmtDate(queryDateEnd)}`, time: fmt() });
        setQueryMethod(null);
        setChipsLocked(true);

        runPropostaSearch({
            initial_date: fmtDate(queryDateStart),
            final_date: fmtDate(queryDateEnd),
            type_date: typeDate,
        });

        setQueryDateStart('');
        setQueryDateEnd('');
    };

    const handleQueryCpfSubmit = () => {
        if (queryCpf.replace(/\D/g, '').length !== 11) return;

        addMsg({ type: 'user', content: queryCpf, time: fmt() });
        setQueryMethod(null);
        setChipsLocked(true);

        runPropostaSearch({ cpf: queryCpf.replace(/\D/g, '') });

        setQueryCpf('');
    };

    const handleQueryFinanceiraSubmit = () => {
        if (!queryFinanceiraId) return;

        const financeira = queryFinanceiras.find((f) => String(f.id) === queryFinanceiraId);
        addMsg({ type: 'user', content: financeira?.name ?? queryFinanceiraId, time: fmt() });
        setChipsLocked(true);
        setQueryFinanceiraSubmitted(true);

        setTimeout(() => {
            addMsg({
                type: 'bot',
                segments: [{ text: 'Perfeito! Agora me informe o número da proposta.' }],
                time: fmt(),
                onStreamDone: () => { setChipsLocked(false); setQueryFinanceiraConfirmed(true); },
            });
        }, 600);
    };

    const handleQueryPropostaSubmit = () => {
        if (!queryPropostaNumero) return;

        const financeira = queryFinanceiras.find((f) => String(f.id) === queryFinanceiraId);
        addMsg({ type: 'user', content: queryPropostaNumero, time: fmt() });
        setQueryMethod(null);
        setQueryFinanceiraSubmitted(false);
        setQueryFinanceiraConfirmed(false);
        setChipsLocked(true);

        runPropostaSearch({ financial: toDigitationFinancial(financeira?.canonicalId), proposal_id: queryPropostaNumero });

        setQueryFinanceiraId('');
        setQueryPropostaNumero('');
    };

    const handleOperation = (op: 'novo' | 'continuar' | 'historico') => {
        if (chipsLocked) return;

        setChipsLocked(true);
        setCurrentChips(null);

        const labels = { novo: 'Novo', continuar: 'Continuar', historico: 'Histórico' };
        addMsg({ type: 'user', content: labels[op], time: fmt() });

        // SB-1953: o "Histórico" passou a ser o das simulações CLT feitas pelo operador.
        // Antes ele pulava para o assistente de Consulta de Propostas — que vai passar a
        // misturar CLT e INSS, exatamente a separação que este ramo preserva.
        if (op === 'historico') {
            setTimeout(() => runHistoricoSearch(), 600);
            return;
        }

        if (op !== 'novo') {
            const msgMap = {
                continuar: 'A opção de continuar uma simulação em andamento estará disponível em breve. Por enquanto, selecione outra opção.',
            } as const;
            setTimeout(() => {
                addMsg({
                    type: 'bot',
                    segments: [{ text: msgMap[op] }],
                    time: fmt(),
                    onStreamDone: () => { setChipsLocked(false); setCurrentChips('operation'); },
                });
            }, 600);
            return;
        }

        setTimeout(() => {
            addMsg({
                type: 'bot',
                segments: [{ text: 'Ok, vamos iniciar um novo fluxo de simulações. Aguarde enquanto eu inicio o módulo...' }],
                time: fmt(),
            });
            setPhase('loading');

            const loadTexts = [
                'Carregando Módulo de Simulações CLT...',
                'Verificando integrações com financeiras...',
                'Preparando formulário de dados...',
                'Módulo pronto! Iniciando...',
            ];
            let i = 0;
            const iv = setInterval(() => {
                i++;
                if (i < loadTexts.length) setLoadingText(loadTexts[i]);
                else clearInterval(iv);
            }, 800);

            setTimeout(async () => {
                const isAvailable = await checkScreeningAvailability();
                if (isAvailable) setPhase('done');
            }, 3400);
        }, 600);
    };

    const handleDataprevToggle = () => {
        if (chipsLocked || dataprevBlocked === null) return;
        setChipsLocked(true);
        setCurrentChips(null);

        addMsg({ type: 'user', content: dataprevBlocked ? 'Desbloquear Dataprev' : 'Bloquear Dataprev', time: fmt() });
        setTimeout(() => {
            addMsg({
                type: 'bot',
                segments: [{ text: `Para qual produto deseja ${dataprevBlocked ? 'desbloquear' : 'bloquear'} o acesso à Dataprev?` }],
                time: fmt(),
                onStreamDone: () => setDataprevOptionsOpen(true),
            });
        }, 600);
    };

    const handleDataprevProduct = async (product: 'clt' | 'inss' | 'ambos') => {
        if (dataprevActionLoading) return;
        setDataprevOptionsOpen(false);

        const label = DATAPREV_PRODUCT_OPTIONS.find((o) => o.value === product)?.label ?? product;
        addMsg({ type: 'user', content: label, time: fmt() });

        if (product === 'inss') {
            setTimeout(() => {
                addMsg({
                    type: 'bot',
                    segments: [{ text: 'O módulo de simulações de INSS estará disponível em breve. Por enquanto, selecione outro produto para continuar.' }],
                    time: fmt(),
                    onStreamDone: () => { setChipsLocked(false); setCurrentChips('product'); },
                });
            }, 600);
            return;
        }

        setDataprevActionLoading(true);
        const nextBlocked = !dataprevBlocked;
        const ok = await setDataprevBlockStatus(nextBlocked);
        setDataprevActionLoading(false);

        setTimeout(() => {
            if (ok) setDataprevBlockedState(nextBlocked);
            addMsg({
                type: 'bot',
                segments: [{
                    text: ok
                        ? `Pronto! O acesso à Dataprev foi ${nextBlocked ? 'bloqueado' : 'desbloqueado'} com sucesso.`
                        : 'Não foi possível atualizar o bloqueio da Dataprev agora. Tente novamente em instantes.',
                }],
                time: fmt(),
                onStreamDone: () => { setChipsLocked(false); setCurrentChips('product'); },
            });
        }, 600);
    };

    if (competenceWindowMessage) {
        return (
            <div className="sim-wrapper">
                <div className="step-loading step-loading--error">
                    <div className="step-loading__icon-error">
                        <BvIcon name="cancel" style={{ width: 100, height: 100, color: '#7779BB' }} />
                    </div>
                    <p className="step-loading__title" style={{ fontSize: '36px' }}>Simulação temporariamente<br />indisponível</p>
                    <p className="step-loading__sub" style={{ color: '#7779BB', fontSize: '18px', whiteSpace: 'pre-line' }}>{competenceWindowMessage}</p>
                    <button
                        style={{
                            marginTop: 16,
                            padding: '10px 32px',
                            background: '#222343',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '10px',
                            fontSize: '13px',
                            fontWeight: 700,
                            cursor: 'pointer',
                        }}
                        onClick={restartToProductSelection}
                    >
                        Entendi
                    </button>
                </div>
            </div>
        );
    }

    if (phase !== 'done') {
        return (
            <div className="sim-wrapper">
                {messages.map((msg) =>
                    msg.type === 'bot' ? (
                        <Fragment key={msg.id}>
                            <div className="sim-prechat-msg">
                                <p className="sim-prechat-text">
                                    <StreamingText segments={msg.segments!} onComplete={msg.onStreamDone} />
                                </p>
                                <span className="sim-prechat-time">{msg.time}</span>
                            </div>
                            {msg.results && msg.results.length > 0 && (
                                <PropostaResultsTable results={msg.results} onSelect={setSelectedProposta} />
                            )}
                            {msg.historico && msg.historico.length > 0 && (
                                <HistoricoResultsTable results={msg.historico} onSimular={handleSimularDoHistorico} />
                            )}
                        </Fragment>
                    ) : (
                        <div key={msg.id} className="sim-user-bubble-wrap">
                            <div className="sim-user-bubble">{msg.content}</div>
                            <span className="sim-prechat-time sim-prechat-time--right">{msg.time}</span>
                        </div>
                    )
                )}

                {currentChips === 'product' && !chipsLocked && (
                    <div className="sim-chips">
                        <button className="sim-chip" onClick={() => handleProduct('clt')}>
                            <img src={Clt} alt="CLT" />
                            Consignado do Trabalhador (CLT)
                        </button>
                        <button className="sim-chip" onClick={() => handleProduct('inss')}>
                            <img src={Inss} alt="INSS" />
                            INSS
                        </button>
                        {canManageDataprev && dataprevBlocked !== null && (
                            <button className="sim-chip" onClick={handleDataprevToggle}>
                                <BvIcon name="lock" width={16} height={16} />
                                {dataprevBlocked ? 'Desbloquear Dataprev' : 'Bloquear Dataprev'}
                            </button>
                        )}
                    </div>
                )}

                {dataprevOptionsOpen && (
                    <div className="ChatQuiz" data-testid="DataprevQuiz">
                        <div className="ChatQuiz__card">
                            <div className="ChatQuiz__top">
                                <span className="ChatQuiz__question">Selecione o produto</span>
                            </div>
                            <ul className="ChatQuiz__options">
                                {DATAPREV_PRODUCT_OPTIONS.map((opt, i) => (
                                    <li
                                        key={opt.value}
                                        className="ChatQuiz__option"
                                        onClick={() => handleDataprevProduct(opt.value)}
                                    >
                                        <span className="ChatQuiz__num">{i + 1}</span>
                                        <span className="ChatQuiz__label">{opt.label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                )}

                {currentChips === 'operation' && !chipsLocked && (
                    <div className="sim-chips">
                        <button className="sim-chip" onClick={() => handleOperation('novo')}><BvIcon name="laptopPlus" />Novo</button>
                        <button className="sim-chip" onClick={() => handleOperation('continuar')}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 32 32" fill="currentColor"><path d="M22.8667 12.5331H13.0333C11.48 12.5331 10.14 13.0531 9.01333 14.0864C7.89333 15.1198 7.32666 16.4064 7.32666 17.9398C7.32666 19.4731 7.88666 20.7598 9.01333 21.7931C10.1333 22.8264 11.4733 23.3464 13.0333 23.3464H22.3667C22.6533 23.3464 22.8933 23.4398 23.0867 23.6331C23.2733 23.8198 23.3733 24.0598 23.3733 24.3531C23.3733 24.6464 23.28 24.8798 23.0867 25.0731C22.8933 25.2664 22.66 25.3598 22.3667 25.3598H13.0667C10.9533 25.3598 9.13999 24.6464 7.61333 23.2264C6.09333 21.8064 5.32666 20.0464 5.32666 17.9598C5.32666 15.8731 6.08666 14.1131 7.61333 12.6931C9.13333 11.2731 10.9533 10.5598 13.0667 10.5598H22.8667L19.7667 7.45976C19.5667 7.25977 19.4667 7.02643 19.4667 6.75977C19.4667 6.4931 19.5667 6.25977 19.7667 6.05977C19.9667 5.85977 20.2 5.75977 20.4667 5.75977C20.7333 5.75977 20.9667 5.85977 21.1667 6.05977L25.9667 10.8598C26.08 10.9731 26.1533 11.0798 26.2 11.1931C26.2467 11.3064 26.2667 11.4264 26.2667 11.5598C26.2667 11.6931 26.2467 11.8131 26.2 11.9264C26.1533 12.0398 26.08 12.1464 25.9667 12.2598L21.1667 17.0598C20.9667 17.2598 20.7333 17.3598 20.4667 17.3598C20.2 17.3598 19.9667 17.2598 19.7667 17.0598C19.5667 16.8598 19.4667 16.6264 19.4667 16.3598C19.4667 16.0931 19.5667 15.8598 19.7667 15.6598L22.8667 12.5598V12.5331Z"/></svg>
                            Continuar
                        </button>
                        <button className="sim-chip" onClick={() => handleOperation('historico')}><BvIcon name="pendingActions" />Histórico</button>
                    </div>
                )}

                {currentChips === 'query' && !chipsLocked && (
                    <div className="sim-chips">
                        <button className="sim-chip" onClick={() => handleQueryMethod('digitacao')}><BvIcon name="today"/>Data de Digitação</button>
                        <button className="sim-chip" onClick={() => handleQueryMethod('alteracao')}><BvIcon name="event"/>Data de Alteração</button>
                        <button className="sim-chip" onClick={() => handleQueryMethod('cpf')}><BvIcon name="badge"/>CPF do Cliente</button>
                        <button className="sim-chip" onClick={() => handleQueryMethod('proposta')}><BvIcon name="assignment"/>Número de Proposta</button>
                    </div>
                )}

                {currentChips === 'finish' && !chipsLocked && (
                    <div className="sim-finish-actions">
                        <button
                            className="sim-finish-button sim-finish-button--ghost"
                            onClick={handleAtualizarConsulta}
                            disabled={isRefreshingPropostas}
                        >
                            {isRefreshingPropostas && <span className="sim-finish-button__spinner" />}
                            Atualizar
                        </button>
                        <button className="sim-finish-button" onClick={handleFinalizarConsulta}>
                            Finalizar
                        </button>
                    </div>
                )}

                {(queryMethod === 'digitacao' || queryMethod === 'alteracao') && (
                    <div className="sim-query-date-filter">
                        <div className="sim-query-date-filter__field">
                            <label>Data inicial</label>
                            <input
                                type="date"
                                value={queryDateStart}
                                onChange={(e) => setQueryDateStart(e.target.value)}
                            />
                        </div>
                        <div className="sim-query-date-filter__field">
                            <label>Data final</label>
                            <input
                                type="date"
                                value={queryDateEnd}
                                onChange={(e) => setQueryDateEnd(e.target.value)}
                            />
                        </div>
                        <button
                            className="sim-query-date-filter__submit"
                            disabled={!queryDateStart || !queryDateEnd}
                            onClick={handleQuerySubmit}
                        >
                            Consultar
                        </button>
                    </div>
                )}

                {queryMethod === 'cpf' && (
                    <div className="sim-query-date-filter">
                        <div className="sim-query-date-filter__field">
                            <label>CPF do cliente</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                placeholder="000.000.000-00"
                                maxLength={14}
                                value={queryCpf}
                                onChange={(e) => setQueryCpf(applyMaskCPF(e.target.value))}
                            />
                        </div>
                        <button
                            className="sim-query-date-filter__submit"
                            disabled={queryCpf.replace(/\D/g, '').length !== 11}
                            onClick={handleQueryCpfSubmit}
                        >
                            Consultar
                        </button>
                    </div>
                )}

                {queryMethod === 'proposta' && !queryFinanceiraConfirmed && !queryFinanceiraSubmitted && (
                    <div className="sim-query-date-filter">
                        <div className="sim-query-date-filter__field">
                            <label>Financeira</label>
                            <select
                                value={queryFinanceiraId}
                                disabled={isLoadingQueryFinanceiras}
                                onChange={(e) => setQueryFinanceiraId(e.target.value)}
                            >
                                <option value="">{isLoadingQueryFinanceiras ? 'Carregando...' : 'Selecione...'}</option>
                                {queryFinanceiras.map((f) => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            className="sim-query-date-filter__submit"
                            disabled={!queryFinanceiraId}
                            onClick={handleQueryFinanceiraSubmit}
                        >
                            Continuar
                        </button>
                    </div>
                )}

                {queryMethod === 'proposta' && queryFinanceiraConfirmed && (
                    <div className="sim-query-date-filter">
                        <div className="sim-query-date-filter__field">
                            <label>Número da proposta</label>
                            <input
                                type="text"
                                placeholder="Ex.: 123456"
                                value={queryPropostaNumero}
                                onChange={(e) => setQueryPropostaNumero(e.target.value)}
                            />
                        </div>
                        <button
                            className="sim-query-date-filter__submit"
                            disabled={!queryPropostaNumero}
                            onClick={handleQueryPropostaSubmit}
                        >
                            Consultar
                        </button>
                    </div>
                )}

                {phase === 'loading' && (
                    <div className="sim-prechat-loading">
                        <div className="sim-prechat-spinner" />
                        <span className="sim-prechat-loading-text">{loadingText}</span>
                    </div>
                )}

                {isSearchingPropostas && (
                    <div className="sim-prechat-loading">
                        <div className="sim-prechat-spinner" />
                        <span className="sim-prechat-loading-text">Buscando propostas...</span>
                    </div>
                )}

                {isSearchingHistorico && (
                    <div className="sim-prechat-loading">
                        <div className="sim-prechat-spinner" />
                        <span className="sim-prechat-loading-text">Buscando histórico de simulações...</span>
                    </div>
                )}

                <ModalDefault
                    title="Dados para Pré-preenchimento"
                    show={!!prefillCliente}
                    onClose={() => setPrefillCliente(null)}
                    sizeModal="md"
                    backgroundColor="#F8F8FC"
                    className="proposta-detail-modal"
                    classNameBody="proposta-detail-modal-body"
                    handleSubmit={handleConfirmarPrefill}
                    buttonText="Iniciar Simulação"
                    disabledSubmit={isConfirmingPrefill}
                    // Rodapé só com a ação principal, como no protótipo — mas o "X" do
                    // cabeçalho precisa ser reativado: por padrão ele acompanha o botão
                    // "Fechar", e sem os dois não sobra como sair do modal pelo mouse.
                    showCloseButton={false}
                    showCloseIcon
                >
                    {prefillCliente && (
                        <div className="proposta-detail-section">
                            <div className="proposta-detail-section__heading">
                                <span className="proposta-detail-section__label">Cliente (recuperado do histórico)</span>
                            </div>
                            <div className="proposta-detail-rows">
                                <div className="proposta-detail-row">
                                    <span className="proposta-detail-row__label">Nome</span>
                                    <span className="proposta-detail-row__value">{prefillCliente.nome_completo || '—'}</span>
                                </div>
                                <div className="proposta-detail-row">
                                    <span className="proposta-detail-row__label">CPF</span>
                                    <span className="proposta-detail-row__value">{prefillCliente.cpf}</span>
                                </div>
                                <div className="proposta-detail-row">
                                    <span className="proposta-detail-row__label">Data de Nascimento</span>
                                    <span className="proposta-detail-row__value">{formatBirthDateBR(prefillCliente.data_nascimento)}</span>
                                </div>
                                <div className="proposta-detail-row">
                                    <span className="proposta-detail-row__label">Telefone</span>
                                    <span className="proposta-detail-row__value">{prefillCliente.telefone || '—'}</span>
                                </div>
                                <div className="proposta-detail-row">
                                    <span className="proposta-detail-row__label">Cidade</span>
                                    <span className="proposta-detail-row__value">{prefillCliente.cidade || '—'}</span>
                                </div>
                                <div className="proposta-detail-row">
                                    <span className="proposta-detail-row__label">Estado</span>
                                    <span className="proposta-detail-row__value">{prefillCliente.estado || '—'}</span>
                                </div>
                            </div>
                            <p className="sim-prefill-note">
                                Confira os dados antes de continuar. Todos seguem editáveis no formulário.
                            </p>
                        </div>
                    )}
                </ModalDefault>

                <ModalDefault
                    title="Espelho da Proposta"
                    show={!!selectedProposta}
                    onClose={() => setSelectedProposta(null)}
                    sizeModal="lg"
                    showFooter={false}
                    scrollable
                    backgroundColor="#F8F8FC"
                    className="proposta-detail-modal"
                    classNameBody="proposta-detail-modal-body"
                >
                    {selectedProposta && (
                        <>
                            <div className="proposta-detail-section">
                                <div className="proposta-detail-section__heading">
                                    <span className="proposta-detail-section__label">Proposta</span>
                                    <span className={`proposta-status-pill proposta-status-pill--${timelineStatusVariant(selectedProposta.status)}`}>
                                        {titleCaseWords(selectedProposta.status)}
                                    </span>
                                </div>
                                <span className="proposta-detail-section__subtitle">{selectedProposta.proposal_number}</span>
                                <div className="proposta-detail-rows">
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Financeira</span>
                                        <span className="proposta-detail-row__value">
                                            {selectedProposta.bancarizadora
                                                ? `${selectedProposta.financeira}_${selectedProposta.bancarizadora}`
                                                : selectedProposta.financeira}
                                        </span>
                                    </div>
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Digitada em</span>
                                        <span className="proposta-detail-row__value">{formatDateTimeHourBR(selectedProposta.created_at)}</span>
                                    </div>
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Atualizada em</span>
                                        <span className="proposta-detail-row__value">{formatDateTimeHourBR(selectedProposta.status_updated_at)}</span>
                                    </div>
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Tabela</span>
                                        <span className="proposta-detail-row__value">{selectedProposta.tabela ?? '-'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="proposta-detail-section">
                                <span className="proposta-detail-section__label">Cliente</span>
                                <div className="proposta-detail-rows">
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Nome</span>
                                        <span className="proposta-detail-row__value">{titleCaseWords(selectedProposta.client_name)}</span>
                                    </div>
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">CPF</span>
                                        <span className="proposta-detail-row__value">{applyMaskCPF(selectedProposta.cpf)}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="proposta-detail-section">
                                <span className="proposta-detail-section__label">Operação</span>
                                <div className="proposta-detail-rows">
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Prazo</span>
                                        <span className="proposta-detail-row__value">{selectedProposta.prazo != null ? `${selectedProposta.prazo}x` : '-'}</span>
                                    </div>
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Valor Total</span>
                                        <span className="proposta-detail-row__value">{formatCurrencyBRL(selectedProposta.valor_total) ?? '-'}</span>
                                    </div>
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Valor Líquido</span>
                                        <span className="proposta-detail-row__value">{formatCurrencyBRL(selectedProposta.valor_liquido) ?? '-'}</span>
                                    </div>
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">IOF</span>
                                        <span className="proposta-detail-row__value">{formatCurrencyBRL(selectedProposta.iof) ?? '-'}</span>
                                    </div>
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Seguro</span>
                                        <span className="proposta-detail-row__value">{formatCurrencyBRL(selectedProposta.seguro) ?? '-'}</span>
                                    </div>
                                    <div className="proposta-detail-row">
                                        <span className="proposta-detail-row__label">Link da Assinatura</span>
                                        <span className="proposta-detail-row__value proposta-detail-row__value--link">{selectedProposta.formalization_url ?? '-'}</span>
                                    </div>
                                </div>
                            </div>

                            {selectedProposta.linha_do_tempo?.length > 0 && (
                                <div className="proposta-detail-timeline">
                                    <h5>Linha do tempo</h5>
                                    <table className="proposta-detail-timeline__table">
                                        <thead>
                                            <tr>
                                                <th>Quando</th>
                                                <th>Status</th>
                                                <th>Mensagem</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedProposta.linha_do_tempo.map((t, i) => (
                                                <tr key={i}>
                                                    <td>{formatDateTimeSecondsBR(t.data)}</td>
                                                    <td>
                                                        <span className={`proposta-status-pill proposta-status-pill--${timelineStatusVariant(t.status)}`}>
                                                            {titleCaseWords(t.status)}
                                                        </span>
                                                    </td>
                                                    <td>{t.mensagem ?? '-'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </>
                    )}
                </ModalDefault>
            </div>
        );
    }

    const hasIntroMessage = step === 1 || step === 3 || (step === 6 && !isLoadingDigitacaoForm && !isValidatingDigitacao);

    return (
        <div className="sim-wrapper">
            {hasIntroMessage && (
                <div className="sim-intro-message" style={step === 3 ? { padding: 0 } : {}}>
                    {step === 1 && (
                        <p className="sim-intro-stream">
                            <StreamingText segments={introStep1} onComplete={() => setFormVisible(true)} instant={fromCache} />
                        </p>
                    )}
                    {step === 3 && (
                        <p className="sim-intro-stream">
                            <StreamingText segments={INTRO_FINANCEIRAS} onComplete={() => {}} />
                        </p>
                    )}
                    {step === 6 && (
                        <p className="sim-intro-stream">
                            <StreamingText segments={INTRO_DIGITACAO} onComplete={() => {}} />
                        </p>
                    )}
                </div>
            )}

            {step === 2 && <Step2Loading type="form" onDone={() => setStep(3)} onBack={() => reset()} />}

            {step === 4.5 && <Step2Loading type="simulate" onDone={() => setStep(5)} onBack={() => setStep(4)} />}

            {formVisible && step !== 2 && step !== 4.5 && (
                <div className="sim-form-fade-in">
                    <div className={`form-card${step === 1 ? ' form-card--clean' : step >= 3 ? ' form-card--chat' : ''}`} data-testid="SimulationBank">
                        <div className="form-card-body">
                            {step === 1 && <Step1Form onAdvance={() => setStep(2)} onBack={() => reset()} />}
                            {step === 3 && <Step3Cards onSimulate={() => setStep(4)} onBack={() => reset()} />}
                            {step === 4 && <Step4Params onSimulate={() => setStep(4.5)} onBack={() => setStep(3)} shouldLoadCache={prevStepRef.current === 5} />}
                            {step === 5 && (
                                <Step4Results
                                    onRestart={restartToProductSelection}
                                    onBack={() => setStep(4)}
                                    onSimulate={() => setStep(4.5)}
                                    onSimulateNewCPF={() => { localStorage.removeItem('@beviajuda:sim-step4-params'); reset(); setStep(1); }}
                                    onProsseguirDigitacao={(oferta) => { setOfertaSelecionada(oferta); setStep(6); }}
                                    onCommissionLoadingChange={setIsCommissionLoading}
                                />
                            )}
                            {step === 6 && <StepDigitacaoForm onAvancar={() => setStep(7)} onBack={() => setStep(5)} />}
                            {step === 7 && <StepConfirmacao onBack={() => setStep(6)} onConfirmado={() => setStep(8)} />}
                            {step === 8 && <StepResultado onFinalizar={restartToProductSelection} onBack={() => setStep(6)} />}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SimulationBank;
